import type { Query, QueryFilters } from "@tanstack/react-query";
import type { ThreadChunkElement, ThreadState } from "@/entities/thread";
import { API_VERSION } from "@/shared/api/version";

export const THREAD_CHUNK_SIZE = 100;
const THREAD_TAIL_STALE_TIME = 15_000;

export const THREAD_CHUNK_QUERY_BEHAVIOR = {
  refetchOnWindowFocus: false,
} as const;

export type ThreadStateQueryKey = readonly [
  "thread",
  string,
  "viewer",
  number,
  "state",
];

export function threadStateQueryKey(
  threadId: string,
  viewerContext: number,
): ThreadStateQueryKey {
  return ["thread", threadId, "viewer", viewerContext, "state"] as const;
}

export function threadArchiveSnapshotQueryKey(
  threadId: string,
  boardSlug: string,
): readonly ["thread", string, "public-archive-snapshot", string] {
  return ["thread", threadId, "public-archive-snapshot", boardSlug] as const;
}

export function threadChunkQueryKey(
  threadId: string,
  chunkNumber?: number,
):
  | readonly ["thread", string, "public-chunk", string]
  | readonly ["thread", string, "public-chunk", string, number] {
  return chunkNumber === undefined
    ? (["thread", threadId, "public-chunk", API_VERSION] as const)
    : (["thread", threadId, "public-chunk", API_VERSION, chunkNumber] as const);
}

export function threadPrivateChunkQueryKey(
  threadId: string,
  viewerContext: number,
  chunkNumber?: number,
):
  | readonly ["thread", string, "viewer", number, "private-chunk"]
  | readonly ["thread", string, "viewer", number, "private-chunk", number] {
  return chunkNumber === undefined
    ? (["thread", threadId, "viewer", viewerContext, "private-chunk"] as const)
    : ([
        "thread",
        threadId,
        "viewer",
        viewerContext,
        "private-chunk",
        chunkNumber,
      ] as const);
}

export function getThreadChunkStaleTime(
  data: readonly unknown[] | undefined,
): number {
  return data?.length === THREAD_CHUNK_SIZE
    ? Number.POSITIVE_INFINITY
    : THREAD_TAIL_STALE_TIME;
}

export function createFailedThreadChunkRefetchFilters(
  threadId: string,
  viewerContext: number,
): QueryFilters {
  return {
    type: "active",
    predicate: (query: Query) => {
      const key = query.queryKey;
      const isPublicChunk =
        key[0] === "thread" && key[1] === threadId && key[2] === "public-chunk";
      const isPrivateChunk =
        key[0] === "thread" &&
        key[1] === threadId &&
        key[2] === "viewer" &&
        key[3] === viewerContext &&
        key[4] === "private-chunk";
      return (
        query.state.status === "error" && (isPublicChunk || isPrivateChunk)
      );
    },
  };
}

export function createOldViewerContextRemovalFilters(
  viewerContext: number,
): QueryFilters {
  return {
    predicate: (query: Query) => {
      const key = query.queryKey;
      return (
        key[0] === "thread" && key[2] === "viewer" && key[3] === viewerContext
      );
    },
  };
}

export function hasCompleteThreadChunkSnapshot(
  chunks: readonly (readonly ThreadChunkElement[] | undefined)[],
  requiredChunkCount: number,
): boolean {
  if (requiredChunkCount < 1 || chunks.length < requiredChunkCount)
    return false;
  if (!chunks[0]?.some((element) => element.seq === 0)) return false;
  return chunks
    .slice(0, requiredChunkCount)
    .every((chunk) => chunk !== undefined);
}

/** Only expose the prefix; an out-of-order arrival must not create a gap. */
export function getContiguousThreadChunks(
  chunks: readonly (readonly ThreadChunkElement[] | undefined)[],
): readonly (readonly ThreadChunkElement[])[] {
  if (!chunks[0]?.some((element) => element.seq === 0)) return [];
  const prefix: (readonly ThreadChunkElement[])[] = [];
  for (const chunk of chunks) {
    if (chunk === undefined) break;
    prefix.push(chunk);
  }
  return prefix;
}

export function resolveThreadQueryError(
  activeError: Error | null,
  chunkError: Error | null,
  isArchiveView: boolean,
  hasCompleteSnapshot: boolean,
): Error | null {
  if (activeError) return activeError;
  if (isArchiveView && hasCompleteSnapshot) return null;
  if (!isArchiveView && !hasCompleteSnapshot) return chunkError;
  return null;
}

type RefetchThreadQueriesInput = {
  fullThreadRefetch: () => Promise<unknown>;
  isArchiveView: boolean;
  refetchQueries: (filters: QueryFilters) => Promise<unknown>;
  stateRefetch: () => Promise<{ data?: ThreadState }>;
  threadId: string;
  viewerContext: number;
};

export async function refetchThreadQueries({
  fullThreadRefetch,
  isArchiveView,
  refetchQueries,
  stateRefetch,
  threadId,
  viewerContext,
}: RefetchThreadQueriesInput): Promise<ThreadState | undefined> {
  if (isArchiveView) {
    await fullThreadRefetch();
    return undefined;
  }
  const statePromise = stateRefetch();
  const chunksPromise = Promise.allSettled([
    refetchQueries(
      createFailedThreadChunkRefetchFilters(threadId, viewerContext),
    ),
  ]);
  const stateResult = await statePromise;
  if (stateResult.data?.archivedAt) {
    // The newly enabled archive observer owns its first snapshot fetch.
    return stateResult.data;
  }
  for (const result of await chunksPromise) {
    if (result.status === "rejected") throw result.reason;
  }
  return stateResult.data;
}
